import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { 
  Zap, CheckCircle, X, Edit, Send, Users, AlertTriangle, Brain, 
  Sparkles, RefreshCw, ChevronDown, ChevronUp, Clock, Target,
  TrendingUp, Network, BookOpen, MessageSquare, BarChart3, Award,
  ShieldAlert, Activity, Lightbulb, Calculator, Flag, User, Search,
  Link2, TriangleAlert, Loader2, Info
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { generateRecommendationSummary, interventionTypeTranslations, safetyStateTranslations } from '@/utils/plainLanguageTranslations';

// Map MDP categories to valid database constraint values
function mapMDPCategoryToDbCategory(mdpCategory: string, interventionType: string): string {
  if (interventionType === 'cluster_challenge_quest') return 'cluster_challenge';
  if (interventionType === 'video_challenge_quest') return 'record_safety_message';
  if (interventionType === 'peer_observation_quest') return 'peer_observation';
  if (interventionType === 'commitment_quest') return 'commitment';
  if (interventionType === 'micro_learning_quest') return 'micro_learning';
  if (interventionType === 'norm_nudge_popup') return 'norm_nudge';
  if (interventionType === 'success_story_popup') return 'success_story';
  if (interventionType === 'recognition_popup') return 'recognition';
  if (interventionType === 'daily_reminder_banner') return 'reminder';
  if (interventionType === 'safety_tip_banner') return 'safety_tip';
  if (interventionType === 'supervisor_meeting') return 'supervisor_escalation';

  const categoryMap: Record<string, string> = {
    'network_intervention': 'record_safety_message',
    'social_proof': 'success_story',
    'commitment_device': 'commitment',
    'gamification': 'recognition',
    'daily_reminder': 'reminder',
    'mentorship': 'peer_observation',
    'micro_learning': 'micro_learning',
    'norm_nudge': 'norm_nudge',
    'recognition': 'recognition',
    'success_story': 'success_story',
  };

  return categoryMap[mdpCategory] || 'motivation';
}

// MDP Analysis structure
interface PolicyAction {
  action_id: string;
  action_name: string;
  delivery_method: string;
  q_value: number;
  policy_probability: number;
  is_selected: boolean;
  is_feasible: boolean;
  reasoning: string;
}

interface MDPAnalysis {
  mdp_state: {
    current: string;
    risk_score: number;
    violations_30d: number;
  };
  selected_action: {
    intervention_type: string;
    name: string;
    q_value?: number;
    expected_value?: number;
    policy_probability?: number;
    breakdown: {
      base_effectiveness: number;
      psychometric_match: number;
      network_match: number;
      violation_relevance: number;
      adjusted_probability: number;
      expected_reward?: number;
      improvement_value?: number;
      cost: number;
      fatigue: number;
    };
    reasoning: string;
  };
  policy_distribution?: PolicyAction[];
  selection_method?: string;
  softmax_beta?: number;
  exploration_floor?: number;
  violation_context: {
    count: number;
    types: string[];
    recent_items: Array<{ category: string; description: string; date: string }>;
  };
  psychometric_factors: string[];
  network_context: {
    role: string;
    cluster_risk: number;
    is_wide_bridge: boolean;
  };
  secondary_recommendation: {
    intervention_type: string;
    name: string;
    q_value?: number;
    expected_value?: number;
    policy_probability?: number;
    reasoning: string;
  } | null;
}

interface Suggestion {
  id: string;
  target_type: 'individual' | 'cluster';
  target_worker_id: string | null;
  target_cluster_id: string | null;
  worker_name?: string;
  worker_team?: string;
  worker_risk_score?: number;
  worker_safety_state?: string;
  cluster_name?: string;
  cluster_risk_score?: number;
  cluster_member_count?: number;
  intervention_type: string;
  trigger_reason: string;
  ai_reasoning: string | null;
  mdp_analysis?: MDPAnalysis & { human_explanation?: string; structured_content?: any } | null;
  suggested_intervention: {
    title?: string;
    message?: string;
    delivery_method?: string;
    category?: string;
    xp_reward?: number;
    learning_points?: string[];
    timing?: string;
    cluster_name?: string;
    member_count?: number;
    risk_score?: number;
    structured_content?: {
      quest_type?: string;
      videoTopic?: string;
      maxDuration?: number;
      observationChecklist?: string[];
      commitmentTemplate?: string;
      slides?: { title: string; content: string }[];
      challengeMetric?: string;
      challengeTarget?: number;
    };
    human_explanation?: string;
  };
  status: string;
  priority: string | null;
  approval_tier: string;
  created_at: string;
}

interface RecommendationStats {
  workerActive: number;
  workerDelivered: number;
  clusterActive: number;
  clusterDelivered: number;
}

interface InterventionRecommendationsProps {
  siteId?: string | null;
}

const InterventionRecommendations = ({ siteId: propSiteId }: InterventionRecommendationsProps = {}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const ACTIVE_STATUSES = ['pending_bs_review', 'pending_hse_review', 'approved', 'pending'] as const;
  const DELIVERED_STATUSES = ['deployed', 'completed'] as const;

  const [activeTab, setActiveTab] = useState<'workers' | 'clusters'>('workers');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [stats, setStats] = useState<RecommendationStats>({ workerActive: 0, workerDelivered: 0, clusterActive: 0, clusterDelivered: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(propSiteId || 'all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<{ open: boolean; suggestion: Suggestion | null }>({ open: false, suggestion: null });
  const [editedMessage, setEditedMessage] = useState('');
  const [delivering, setDelivering] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'delivered'>('active');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSuggestions();
  }, [selectedSite, statusFilter]);

  const fetchSuggestions = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('intervention_suggestions')
        .select(`
          *,
          worker:worker_profiles!target_worker_id(display_name, team, current_risk_score, site_id, safety_state),
          cluster:worker_clusters!target_cluster_id(cluster_name, cluster_risk_score, member_ids)
        `)
        .order('created_at', { ascending: false });

      if (statusFilter === 'active') {
        query = query.in('status', ['pending_bs_review', 'pending_hse_review', 'approved', 'pending']);
      } else {
        query = query.in('status', ['deployed', 'completed']);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Filter by site if needed (for workers only)
      let filtered = data || [];
      if (selectedSite !== 'all') {
        filtered = filtered.filter((s: any) => 
          s.target_type === 'cluster' || s.worker?.site_id === selectedSite
        );
      }

      const transformed: Suggestion[] = filtered.map((s: any) => {
        let mdpAnalysis: MDPAnalysis | null = null;
        if (s.ai_reasoning) {
          try {
            mdpAnalysis = JSON.parse(s.ai_reasoning);
          } catch {
            // Not JSON
          }
        }

        return {
          id: s.id,
          target_type: s.target_type || 'individual',
          target_worker_id: s.target_worker_id,
          target_cluster_id: s.target_cluster_id,
          worker_name: s.worker?.display_name || 'Unknown',
          worker_team: s.worker?.team || '',
          worker_risk_score: s.worker?.current_risk_score || 0,
          worker_safety_state: s.worker?.safety_state || 'safe',
          cluster_name: s.cluster?.cluster_name || s.suggested_intervention?.cluster_name || 'Unknown Cluster',
          cluster_risk_score: s.cluster?.cluster_risk_score || s.suggested_intervention?.risk_score || 0,
          cluster_member_count: s.cluster?.member_ids?.length || s.suggested_intervention?.member_count || 0,
          intervention_type: s.intervention_type,
          trigger_reason: s.trigger_reason,
          ai_reasoning: s.ai_reasoning,
          mdp_analysis: mdpAnalysis,
          suggested_intervention: s.suggested_intervention || {},
          status: s.status,
          priority: s.priority,
          approval_tier: s.approval_tier,
          created_at: s.created_at,
        };
      });

      setSuggestions(transformed);

      // Calculate stats
      const { data: allSuggestions } = await supabase
        .from('intervention_suggestions')
        .select('status, target_type, worker:worker_profiles!target_worker_id(site_id)');

      let relevant = allSuggestions || [];
      if (selectedSite !== 'all') {
        relevant = relevant.filter((s: any) => 
          s.target_type === 'cluster' || s.worker?.site_id === selectedSite
        );
      }

      const workerSuggestions = relevant.filter((s: any) => s.target_type !== 'cluster');
      const clusterSuggestions = relevant.filter((s: any) => s.target_type === 'cluster');

      setStats({
        workerActive: workerSuggestions.filter((s: any) => ['pending_bs_review', 'pending_hse_review', 'approved', 'pending'].includes(s.status)).length,
        workerDelivered: workerSuggestions.filter((s: any) => ['deployed', 'completed'].includes(s.status)).length,
        clusterActive: clusterSuggestions.filter((s: any) => ['pending_bs_review', 'pending_hse_review', 'approved', 'pending'].includes(s.status)).length,
        clusterDelivered: clusterSuggestions.filter((s: any) => ['deployed', 'completed'].includes(s.status)).length,
      });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast({ title: 'Error', description: 'Failed to load recommendations', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const mapMDPToWizardCategory = (mdpType: string): string => {
    const map: Record<string, string> = {
      'norm_nudge_popup': 'norm_nudge',
      'success_story_popup': 'success_story',
      'recognition_popup': 'recognition',
      'daily_reminder_banner': 'reminder',
      'commitment_quest': 'commitment',
      'video_challenge_quest': 'record_safety_message',
      'peer_observation_quest': 'peer_observation',
      'cluster_challenge_quest': 'cluster_challenge',
      'supervisor_meeting': 'commitment',
      'team_safety_huddle': 'commitment',
      'peer_influence_campaign': 'norm_nudge',
      'champion_intervention': 'commitment',
      'collective_commitment': 'commitment',
    };
    return map[mdpType] || 'commitment';
  };

  const handleDeliver = async (suggestion: Suggestion) => {
    const deliveryMethod = suggestion.suggested_intervention.delivery_method || 'popup';
    
    // Handle cluster recommendations - redirect to CreateIntervention with prefill
    if (suggestion.target_type === 'cluster') {
      const mappedCategory = mapMDPToWizardCategory(suggestion.intervention_type);
      
      // For supervisor action on clusters, create supervisor task directly
      if (deliveryMethod === 'supervisor_action') {
        try {
          const { data: clusterData } = await supabase
            .from('worker_clusters')
            .select('company_id')
            .eq('id', suggestion.target_cluster_id)
            .single();

          const { error } = await supabase
            .from('supervisor_tasks')
            .insert({
              title: suggestion.suggested_intervention.title || `Team Intervention: ${suggestion.cluster_name}`,
              description: `${suggestion.suggested_intervention.message || suggestion.ai_reasoning}\n\n**Why This Recommendation:**\n${suggestion.trigger_reason}`,
              priority: suggestion.priority || 'medium',
              task_category: 'coach',
              source_type: 'cluster_recommendation',
              source_id: suggestion.id,
              status: 'pending',
              company_id: clusterData?.company_id,
              source_context: {
                cluster_id: suggestion.target_cluster_id,
                cluster_name: suggestion.cluster_name,
                cluster_risk_score: suggestion.cluster_risk_score,
                member_count: suggestion.cluster_member_count,
                ai_explanation: suggestion.ai_reasoning
              }
            });

          if (error) throw error;

          await supabase
            .from('intervention_suggestions')
            .update({ status: 'deployed' })
            .eq('id', suggestion.id);

          toast({ title: 'Success', description: 'Team coaching task created for supervisor' });
          fetchSuggestions();
          return;
        } catch (error) {
          console.error('Error creating supervisor task:', error);
          toast({ title: 'Error', description: 'Failed to create supervisor task', variant: 'destructive' });
          return;
        }
      }

      // Redirect to CreateIntervention with cluster prefill
      navigate('/admin/create-intervention', {
        state: {
          prefill: {
            deliveryMethod: deliveryMethod === 'quest' ? 'quest' : 'popup',
            category: mappedCategory,
            targetType: 'cluster',
            targetClusterId: suggestion.target_cluster_id,
            title: suggestion.suggested_intervention.title || '',
            message: suggestion.suggested_intervention.message || '',
            xpReward: suggestion.suggested_intervention.xp_reward || 15,
            suggestionId: suggestion.id
          }
        }
      });
      return;
    }

    // Handle worker recommendations (existing logic)
    if (deliveryMethod === 'supervisor_action') {
      try {
        const { data: workerData } = await supabase
          .from('worker_profiles')
          .select('company_id, site_id')
          .eq('id', suggestion.target_worker_id)
          .single();

        const { error } = await supabase
          .from('supervisor_tasks')
          .insert({
            title: suggestion.suggested_intervention.title || `Coach ${suggestion.worker_name}`,
            description: suggestion.suggested_intervention.message || suggestion.ai_reasoning,
            priority: suggestion.priority || 'medium',
            task_category: 'coach',
            source_type: 'mdp_recommendation',
            source_id: suggestion.id,
            status: 'pending',
            target_worker_id: suggestion.target_worker_id,
            company_id: workerData?.company_id,
            site_id: workerData?.site_id,
            source_context: {
              worker_name: suggestion.worker_name,
              worker_risk_score: suggestion.worker_risk_score,
              worker_safety_state: suggestion.worker_safety_state,
              ai_explanation: suggestion.mdp_analysis?.selected_action?.reasoning || suggestion.ai_reasoning
            }
          });

        if (error) throw error;

        await supabase
          .from('intervention_suggestions')
          .update({ status: 'deployed' })
          .eq('id', suggestion.id);

        toast({ title: 'Success', description: 'Coaching task created for supervisor' });
        fetchSuggestions();
        return;
      } catch (error) {
        console.error('Error creating supervisor task:', error);
        toast({ title: 'Error', description: 'Failed to create supervisor task', variant: 'destructive' });
        return;
      }
    }
    
    const mappedCategory = mapMDPToWizardCategory(suggestion.intervention_type);
    const structuredContent = suggestion.suggested_intervention.structured_content || {};
    
    if (suggestion.intervention_type === 'micro_learning_quest') {
      navigate('/admin/create-learning', {
        state: {
          prefill: {
            targetType: 'individual',
            targetWorkerId: suggestion.target_worker_id,
            trainingDescription: suggestion.suggested_intervention.message || '',
            title: suggestion.suggested_intervention.title || '',
            suggestionId: suggestion.id
          }
        }
      });
      return;
    }
    
    navigate('/admin/create-intervention', {
      state: {
        prefill: {
          deliveryMethod: deliveryMethod === 'quest' ? 'quest' : deliveryMethod === 'banner' ? 'banner' : 'popup',
          category: mappedCategory,
          targetType: 'individual',
          targetWorkerId: suggestion.target_worker_id,
          title: suggestion.suggested_intervention.title || '',
          message: suggestion.suggested_intervention.message || '',
          xpReward: suggestion.suggested_intervention.xp_reward || 10,
          structuredContent: structuredContent,
          suggestionId: suggestion.id
        }
      }
    });
  };

  const handleEditSave = async () => {
    if (!editDialog.suggestion) return;

    try {
      const updated = {
        ...editDialog.suggestion.suggested_intervention,
        message: editedMessage,
      };

      await supabase
        .from('intervention_suggestions')
        .update({ 
          suggested_intervention: updated,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editDialog.suggestion.id);

      toast({ title: 'Updated', description: 'Intervention message updated' });
      setEditDialog({ open: false, suggestion: null });
      fetchSuggestions();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case 'critical': return 'bg-red-600/10 text-red-600 border-red-600/20';
      case 'high': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-success/10 text-success border-success/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_bs_review': 
      case 'pending_hse_review': 
      case 'pending':
        return <Badge variant="outline" className="border-warning/30 text-warning">Pending Review</Badge>;
      case 'approved': 
        return <Badge className="bg-success/20 text-success">Ready to Deliver</Badge>;
      case 'rejected': 
        return <Badge variant="destructive">Rejected</Badge>;
      case 'deployed': 
      case 'completed':
        return <Badge className="bg-primary/20 text-primary">Delivered</Badge>;
      default: 
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInterventionIcon = (type: string) => {
    switch (type) {
      case 'commitment_device': 
      case 'commitment_quest':
        return <Target className="h-4 w-4" />;
      case 'micro_learning': 
      case 'micro_learning_quest':
        return <BookOpen className="h-4 w-4" />;
      case 'norm_nudge':
      case 'norm_nudge_popup':
        return <Users className="h-4 w-4" />;
      case 'supervisor_meeting':
      case 'team_safety_huddle':
        return <AlertTriangle className="h-4 w-4" />;
      case 'cluster_challenge_quest':
      case 'cluster_challenge':
      case 'collective_commitment':
        return <Network className="h-4 w-4" />;
      case 'peer_influence_campaign':
      case 'champion_intervention':
        return <Sparkles className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  const getSafetyStateBadge = (state: string) => {
    switch (state) {
      case 'chronic': return <Badge variant="destructive">Chronic</Badge>;
      case 'high_risk': return <Badge className="bg-orange-500/20 text-orange-600 dark:text-orange-400">High Risk</Badge>;
      case 'at_risk': return <Badge className="bg-warning/20 text-warning">At Risk</Badge>;
      case 'safe': return <Badge className="bg-success/20 text-success">Safe</Badge>;
      default: return <Badge variant="outline">{state}</Badge>;
    }
  };

  const getRiskBadge = (score: number) => {
    if (score >= 60) return <Badge variant="destructive">{score}% Risk</Badge>;
    if (score >= 40) return <Badge className="bg-warning/20 text-warning">{score}%</Badge>;
    return <Badge className="bg-success/20 text-success">{score}%</Badge>;
  };

  // Filter suggestions by tab and search
  const filteredSuggestions = suggestions.filter(s => {
    const matchesTab = activeTab === 'workers' ? s.target_type !== 'cluster' : s.target_type === 'cluster';
    const matchesSearch = searchQuery === '' || 
      (s.worker_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (s.cluster_name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (s.intervention_type?.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const renderSuggestionCard = (suggestion: Suggestion) => {
    const isCluster = suggestion.target_type === 'cluster';
    const name = isCluster ? suggestion.cluster_name : suggestion.worker_name;
    const riskScore = isCluster ? suggestion.cluster_risk_score : suggestion.worker_risk_score;

    return (
      <Card 
        key={suggestion.id}
        className={cn(
          "transition-all",
          suggestion.priority === 'high' && "border-l-4 border-l-destructive",
          suggestion.priority === 'medium' && "border-l-4 border-l-warning",
          selectedIds.has(suggestion.id) && "ring-2 ring-primary"
        )}
      >
        <CardContent className="p-4">
          {/* Main Row */}
          <div className="flex items-start gap-4">
            <Checkbox 
              checked={selectedIds.has(suggestion.id)}
              onCheckedChange={() => toggleSelect(suggestion.id)}
              onClick={(e) => e.stopPropagation()}
            />

            <div 
              className="flex-1 cursor-pointer"
              onClick={() => setExpandedId(expandedId === suggestion.id ? null : suggestion.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {isCluster ? <Users className="h-4 w-4 text-primary" /> : <User className="h-4 w-4" />}
                  {getInterventionIcon(suggestion.intervention_type)}
                  <span className="font-medium">{name}</span>
                  {!isCluster && suggestion.worker_team && (
                    <span className="text-sm text-muted-foreground">• {suggestion.worker_team}</span>
                  )}
                  {isCluster && suggestion.cluster_member_count && (
                    <span className="text-sm text-muted-foreground">• {suggestion.cluster_member_count} members</span>
                  )}
                  {!isCluster && suggestion.worker_safety_state && getSafetyStateBadge(suggestion.worker_safety_state)}
                  {riskScore !== undefined && riskScore >= 40 && getRiskBadge(riskScore)}
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(suggestion.status)}
                  <Badge variant="outline" className={getPriorityColor(suggestion.priority)}>
                    {suggestion.priority || 'normal'}
                  </Badge>
                  {expandedId === suggestion.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Reasoning summary */}
              <p className="text-sm text-muted-foreground line-clamp-2">
                {suggestion.mdp_analysis?.selected_action?.reasoning || suggestion.trigger_reason}
              </p>

              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {format(new Date(suggestion.created_at), 'MMM d, HH:mm')}
                </span>
                <span className="capitalize">Type: {suggestion.intervention_type.replace(/_/g, ' ')}</span>
                <span className="capitalize">Delivery: {suggestion.suggested_intervention.delivery_method || 'popup'}</span>
              </div>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedId === suggestion.id && (
            <div className="mt-4 pt-4 border-t space-y-4">
              {/* Plain Language Summary - Always show first */}
              {(() => {
                const summary = generateRecommendationSummary({
                  worker_name: suggestion.worker_name,
                  cluster_name: suggestion.cluster_name,
                  target_type: suggestion.target_type,
                  intervention_type: suggestion.intervention_type,
                  worker_safety_state: suggestion.worker_safety_state,
                  worker_risk_score: suggestion.worker_risk_score,
                  cluster_risk_score: suggestion.cluster_risk_score,
                  cluster_member_count: suggestion.cluster_member_count,
                  trigger_reason: suggestion.trigger_reason,
                  mdp_analysis: suggestion.mdp_analysis,
                });
                
                return (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                    {/* What to do */}
                    <div>
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" />
                        Apa yang perlu dilakukan?
                      </p>
                      <p className="text-sm">{summary.whatToDo}</p>
                    </div>
                    
                    {/* Why this worker/cluster */}
                    {summary.whyThisWorker.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          Mengapa {isCluster ? 'tim' : 'pekerja'} ini perlu diperhatikan?
                        </p>
                        <ul className="text-sm space-y-1">
                          {summary.whyThisWorker.map((reason, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-1">•</span>
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Why this intervention */}
                    <div>
                      <p className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-success" />
                        Mengapa intervensi ini cocok?
                      </p>
                      <p className="text-sm">{summary.whyThisIntervention}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Technical Details - Hidden by default */}
              {!isCluster && suggestion.mdp_analysis && (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Lihat Detail Teknis
                      </span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {suggestion.mdp_analysis.mdp_state && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            MDP State
                          </p>
                          <div className="flex items-center gap-2">
                            {getSafetyStateBadge(suggestion.mdp_analysis.mdp_state.current)}
                            <span className="text-sm text-muted-foreground">
                              Risk: {suggestion.mdp_analysis.mdp_state.risk_score || 0}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {suggestion.mdp_analysis.mdp_state.violations_30d || 0} violations (30d)
                          </p>
                        </div>
                      )}

                      {suggestion.mdp_analysis.network_context && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                            <Network className="h-3 w-3" />
                            Network Role
                          </p>
                          <Badge variant="outline" className="capitalize">
                            {suggestion.mdp_analysis.network_context.role || 'Unknown'}
                          </Badge>
                          {suggestion.mdp_analysis.network_context.is_wide_bridge && (
                            <Badge variant="outline" className="ml-1 bg-primary/10">Wide Bridge</Badge>
                          )}
                        </div>
                      )}

                      <div className="bg-muted/30 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Brain className="h-3 w-3" />
                          Psychometric Factors
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(suggestion.mdp_analysis.psychometric_factors) 
                            ? suggestion.mdp_analysis.psychometric_factors 
                            : []
                          ).slice(0, 3).map((factor, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs capitalize">
                              {String(factor)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* EV Breakdown */}
                    {suggestion.mdp_analysis.selected_action?.breakdown && (
                      <div className="bg-muted/20 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Calculator className="h-3 w-3" />
                          Expected Value Breakdown
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Base Effectiveness:</span>
                            <span className="ml-2 font-mono">{((suggestion.mdp_analysis.selected_action.breakdown.base_effectiveness || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Psychometric Match:</span>
                            <span className={cn("ml-2 font-mono", (suggestion.mdp_analysis.selected_action.breakdown.psychometric_match || 1) > 1 ? "text-success" : "")}>
                              ×{(suggestion.mdp_analysis.selected_action.breakdown.psychometric_match || 1).toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Network Match:</span>
                            <span className={cn("ml-2 font-mono", (suggestion.mdp_analysis.selected_action.breakdown.network_match || 1) > 1 ? "text-success" : "")}>
                              ×{(suggestion.mdp_analysis.selected_action.breakdown.network_match || 1).toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Violation Relevance:</span>
                            <span className="ml-2 font-mono">
                              ×{(suggestion.mdp_analysis.selected_action.breakdown.violation_relevance || 1).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="border-t pt-2 mt-2">
                          <div className="flex justify-between text-sm font-medium">
                            <span>Expected Value:</span>
                            <span className="text-primary font-mono">{(suggestion.mdp_analysis.selected_action.expected_value || suggestion.mdp_analysis.selected_action.q_value || 0).toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Suggested Content */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Generated Content</p>
                <p className="font-medium">{suggestion.suggested_intervention.title || 'Safety Intervention'}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {suggestion.suggested_intervention.message || 'Default intervention message'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  {suggestion.suggested_intervention.xp_reward && (
                    <Badge variant="outline">
                      +{suggestion.suggested_intervention.xp_reward} XP
                    </Badge>
                  )}
                  <Badge variant="outline" className="capitalize">
                    {suggestion.suggested_intervention.delivery_method || 'popup'}
                  </Badge>
                </div>
              </div>

              {/* Actions */}
              {(['pending_bs_review', 'pending_hse_review', 'approved', 'pending'] as const).includes(suggestion.status as any) && (
                <div className="flex items-center justify-end gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setEditedMessage(suggestion.suggested_intervention.message || '');
                      setEditDialog({ open: true, suggestion });
                    }}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => handleDeliver(suggestion)}
                    disabled={delivering}
                    className="gap-1"
                  >
                    <Send className="h-4 w-4" />
                    Deliver Now
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            AI Recommendations Hub
          </h2>
          <p className="text-muted-foreground">
            Unified view of worker and team intervention suggestions
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
          <Button variant="outline" size="sm" onClick={fetchSuggestions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'workers' | 'clusters')}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="workers" className="gap-2">
            <User className="h-4 w-4" />
            Individual Workers
            <Badge variant="secondary" className="ml-1">{stats.workerActive}</Badge>
          </TabsTrigger>
          <TabsTrigger value="clusters" className="gap-2">
            <Users className="h-4 w-4" />
            Team Clusters
            <Badge variant="secondary" className="ml-1">{stats.clusterActive}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Stats & Filters */}
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card 
              className={cn("cursor-pointer transition-all", statusFilter === 'active' && "ring-2 ring-primary")}
              onClick={() => setStatusFilter('active')}
            >
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Pending Delivery</p>
                <p className="text-3xl font-bold text-warning">
                  {activeTab === 'workers' ? stats.workerActive : stats.clusterActive}
                </p>
              </CardContent>
            </Card>
            <Card 
              className={cn("cursor-pointer transition-all", statusFilter === 'delivered' && "ring-2 ring-primary")}
              onClick={() => setStatusFilter('delivered')}
            >
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Delivered</p>
                <p className="text-3xl font-bold text-primary">
                  {activeTab === 'workers' ? stats.workerDelivered : stats.clusterDelivered}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${activeTab === 'workers' ? 'workers' : 'clusters'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <TabsContent value="workers" className="mt-4 space-y-3">
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map(renderSuggestionCard)
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <User className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No worker recommendations to review</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate recommendations from Worker Diagnostics
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="clusters" className="mt-4 space-y-3">
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map(renderSuggestionCard)
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No cluster recommendations to review</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate recommendations from Cluster Diagnostics
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, suggestion: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Intervention Message</DialogTitle>
            <DialogDescription>
              Customize the message before delivery for {editDialog.suggestion?.target_type === 'cluster' 
                ? editDialog.suggestion?.cluster_name 
                : editDialog.suggestion?.worker_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Title</p>
              <p className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                {editDialog.suggestion?.suggested_intervention.title || 'Safety Intervention'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Message</p>
              <Textarea 
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                rows={4}
                placeholder="Enter intervention message..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, suggestion: null })}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InterventionRecommendations;
